/**
 * Reading the ground (M3-08).
 *
 * The skill layer that turns "walk until a wolf spawns" into *pursuit*. Things
 * that move through the valley leave sign behind them — a print in soft ground,
 * a broken line through the bracken, droppings at a den mouth, blood off
 * something that is hurt — and the sign says which way they went and roughly
 * how long ago. The Great Hunt in M5 is structurally a tracking quest, so this
 * is where its centrepiece verb gets built.
 *
 * Three rules shape everything here.
 *
 * **The ground is the UI.** There is no minimap arrow and no highlighted quest
 * trail, and there never will be. What a player gets is a faint mark on a tile
 * and a sentence when they examine it; working out where the trail goes is the
 * activity, not a thing the interface does on their behalf. That is also why
 * `examine` is the reading verb rather than a `track` command — M3-04 made
 * examine layered precisely so a system like this could add a clause.
 *
 * **Sign is emitted by moving, not by a system that watches.** Every step by
 * every actor — player and animal alike — passes through `moveHandler`, and
 * that is where sign is laid. A system that diffed positions each tick would
 * have to reconstruct the direction it already knew, and would silently miss
 * every future way of moving something.
 *
 * **It goes cold.** Sign decays on the hour, faster in rain, faster still on
 * ground that does not hold a mark. A trail crossed by a downpour is a trail
 * you have lost, and that is the drama rather than a frustration: it is what
 * makes setting off after a wounded stag *now* different from setting off in
 * the morning.
 *
 * Players leave sign too, deliberately. Trailing a wounded rival back to their
 * camp is a thing this game should permit — the log giveth and the log taketh
 * away.
 */
import { DIRECTIONS } from './actor.js';
import { ATTRIBUTE_RANGE } from './body.js';
import { tileDefAt } from './map.js';
import { TICKS_PER_GAME_DAY, TICKS_PER_GAME_HOUR } from './time.js';
import { isRaining, wetnessOf } from './weather.js';
/* -------------------------------------------------------------------------- *
 * The record
 * -------------------------------------------------------------------------- */
/**
 * What kind of mark this is.
 *
 * Four rather than one, because they decay differently and read differently: a
 * print is in the ground and rain takes it, blood is *on* things and rain takes
 * it more slowly, droppings sit where they fell, and a trail is bent vegetation
 * that springs back on its own schedule.
 */
export const SIGN_KINDS = ['print', 'trail', 'droppings', 'blood'];
/**
 * Position *and kind* are identity.
 *
 * One of each kind per tile, so a stag that walks over its own prints leaves
 * one refreshed print rather than a growing pile — which is what keeps the
 * table bounded on a 65,536-tile map without anything having to sweep it. Blood
 * and a print on the same tile are still two records, because they are two
 * different things to find.
 */
export function signKey(x, y, kind) {
    return `${x},${y},${kind}`;
}
/* -------------------------------------------------------------------------- *
 * What the ground will hold
 * -------------------------------------------------------------------------- */
/**
 * How well each material takes an impression, as a multiplier on strength.
 *
 * Asked of the *material* rather than the tile kind — M3-04's materials-lite
 * doing the job it exists for. Every bog tile worldgen ever adds holds a print
 * without being named here, and the day M3 adds a new soft biome it inherits
 * the rule for free.
 *
 * `0` means the ground takes nothing at all: you cannot track anything across
 * bare rock, which is a real fact about tracking and a real tactical option for
 * anything being tracked.
 */
export const GROUND_HOLD = {
    nothing: 0,
    stone: 0,
    metal: 0,
    water: 0,
    // Soft, wet and holds a shape. The best tracking ground in the valley.
    peat: 1,
    sand: 0.9,
    earth: 0.6,
    plant: 0.45,
    // A worn path is beaten hard — which is the irony of following somebody
    // along the easiest route, and the reason a path is worth taking.
    wood: 0.2,
};
export function groundHold(map, x, y) {
    return GROUND_HOLD[tileDefAt(map, x, y).material] ?? 0;
}
/* -------------------------------------------------------------------------- *
 * Laying it down
 * -------------------------------------------------------------------------- */
/**
 * Chance a walking creature leaves a print on a tile that would hold one.
 *
 * Not 1, and the reason is the table rather than the fiction: at ten ticks a
 * second a creature crossing the commote would otherwise write two hundred
 * records a minute, and a trail with a mark on literally every tile is also a
 * trail with no reading skill in it. A gappy trail is one you have to cast
 * about for, which is the activity.
 */
export const PRINT_CHANCE = 0.34;
/** Fleeing leaves far more: a running animal tears the ground and the bracken. */
export const FLEEING_MULTIPLIER = 2.5;
/** How much blood an open wound drips per tile, as strength. */
export const BLOOD_PER_TILE = 0.5;
/**
 * The bleed rate below which nothing shows on the ground.
 *
 * A clotting scratch does not leave a trail. Above it, the M1-04 bleed clock
 * becomes a thing in the world that somebody else can follow — which is the
 * ticket's "the bleed clock made visible", and the mechanic that makes
 * breaking off a fight a decision rather than an escape.
 */
export const BLOOD_THRESHOLD = 2;
/* -------------------------------------------------------------------------- *
 * How heavy the thing was (M3-10)
 * -------------------------------------------------------------------------- */
/**
 * Frame taken as the average, so a man's print is the unit everything else is
 * read against. `ATTRIBUTE_RANGE.average` rather than a literal 10, because the
 * day the attribute scale moves this should move with it.
 */
const AVERAGE_FRAME = ATTRIBUTE_RANGE.average;
/** Faintest and heaviest a mark may be scaled by the body that left it. */
export const MASS_FLOOR = 0.4;
export const MASS_CEILING = 1.25;
/**
 * How heavily this body marks the ground, as a multiplier on strength.
 *
 * Read off `frame` — the attribute that already means "how big is it" — rather
 * than a mass field added to the body plan, because a second number saying the
 * same thing is a second number that can disagree with the first. A rabbit's
 * frame of 2 leaves a print at the floor; a stag's 13 leaves one plainer than a
 * man's, which is exactly what a novice tracker needs to tell a trail worth
 * following from one that is not.
 *
 * Square root rather than a power: it gives the curve the right shape — the
 * difference between a rabbit and a wolf matters more than the difference between
 * a stag and a boar — and `Math.sqrt` is exact under IEEE-754 on every engine,
 * where `Math.pow` is implementation-defined and would put a float that differs
 * between machines into world state.
 */
export function massFactor(frame) {
    if (!Number.isFinite(frame) || frame <= 0)
        return MASS_FLOOR;
    const scaled = Math.sqrt(frame / AVERAGE_FRAME);
    return Math.max(MASS_FLOOR, Math.min(MASS_CEILING, scaled));
}
/** The same, off whatever body the actor has. `1` for anything bodiless. */
export function massOf(combatant) {
    if (!combatant)
        return 1;
    return massFactor(combatant.loadout.attributes.frame);
}
/**
 * What one step leaves behind, if anything.
 *
 * Pure, and takes the numbers rather than the world, so the emission rule can
 * be tested across every ground and gait without building a valley.
 */
export function emissionFor(options) {
    const emissions = [];
    const gait = options.fleeing ? FLEEING_MULTIPLIER : 1;
    const mass = options.mass ?? 1;
    const chance = Math.min(1, PRINT_CHANCE * gait * options.hold);
    if (options.hold > 0 && options.roll < chance) {
        // Fleeing through vegetation breaks it; on open ground it is still a print.
        // Mass scales how *deep* the mark is rather than how often one is left: a
        // rabbit crosses a bog as often as a stag does, and leaves less behind.
        emissions.push({ kind: 'print', strength: clamp01(options.hold * gait * mass) });
    }
    // Blood does not care what the ground is made of. It falls on rock as
    // readily as on peat, which is exactly why a bleeding quarry cannot lose you
    // by crossing a crag — the one thing that beats good tracking ground.
    if (options.bleedRate >= BLOOD_THRESHOLD) {
        emissions.push({
            kind: 'blood',
            strength: clamp01(BLOOD_PER_TILE * Math.min(2, options.bleedRate / BLOOD_THRESHOLD)),
        });
    }
    return emissions;
}
/**
 * Write one step's sign into the world.
 *
 * Refreshes rather than accumulates: a new mark of the same kind on the same
 * tile replaces the old one, taking the greater strength. Two animals along one
 * game trail leave one trail, which is both true and what keeps the table small.
 */
export function layStep(signs, actor, speciesId, direction, tick, emissions) {
    for (const emission of emissions) {
        const key = signKey(actor.x, actor.y, emission.kind);
        const existing = signs[key];
        signs[key] = {
            x: actor.x,
            y: actor.y,
            kind: emission.kind,
            speciesId,
            direction,
            at: tick,
            strength: existing && existing.speciesId === speciesId
                ? Math.max(existing.strength, emission.strength)
                : emission.strength,
        };
    }
}
/* -------------------------------------------------------------------------- *
 * Going cold
 * -------------------------------------------------------------------------- */
/**
 * How much strength each kind loses per game hour in fair weather.
 *
 * Tuned so a print in good ground lasts most of a day and blood lasts about
 * two — which is the span that makes "go after it now or lose it" a real
 * choice rather than a formality, and short enough that the valley is not
 * eventually paved in month-old sign.
 */
export const DECAY_PER_HOUR = {
    print: 0.05,
    trail: 0.07,
    droppings: 0.02,
    blood: 0.025,
};
/**
 * What the weather does to that rate, per kind.
 *
 * Rain roughly triples the loss of a print — it is an impression in soft ground
 * and rain is what fills it in — and barely touches blood, which is *on* the
 * bracken rather than in the earth and needs washing rather than smoothing. Fog
 * preserves everything a little: still, wet air and no sun.
 *
 * That asymmetry is the mechanic. After a downpour the only thing left to
 * follow is what is bleeding, which is why wounding your quarry matters and why
 * a hunter watches the sky.
 */
export function weatherDecay(weather, kind) {
    const base = DECAY_PER_HOUR[kind];
    if (weather.kind === 'fog')
        return base * 0.6;
    if (isRaining(weather.kind)) {
        const hard = weather.kind === 'heavy-rain' ? 1.6 : 1;
        return kind === 'blood' ? base * (1 + 0.35 * hard) : base * (1 + 2 * hard);
    }
    // Dry ground cracks and crumbles; a print in baked mud does not last either.
    const dryness = 1 - wetnessOf(weather);
    return base * (1 + dryness * 0.4);
}
/** Strength below which a mark is gone and the record is dropped. */
export const FADED = 0.05;
/**
 * An hour of weather over every mark in the valley.
 *
 * Deletes what has faded, which is the only thing keeping this table from
 * growing without bound over a server's life.
 */
export function decaySigns(signs, weather) {
    let removed = 0;
    for (const key of Object.keys(signs)) {
        const sign = signs[key];
        if (!sign)
            continue;
        sign.strength -= weatherDecay(weather, sign.kind);
        if (sign.strength <= FADED) {
            delete signs[key];
            removed += 1;
        }
    }
    return removed;
}
/* -------------------------------------------------------------------------- *
 * Reading it
 * -------------------------------------------------------------------------- */
/**
 * How far a tracker can spot sign, in tiles.
 *
 * One at no familiarity, three at full — M3-03's forage counter in a second
 * coat, and deliberately the same shape so that "getting better at something"
 * means one thing in this game. Spotting is what familiarity buys first,
 * because the difference between finding a trail and walking past it is the
 * difference between a hunt and a walk.
 */
export const SPOT_RANGE = { base: 1, practised: 3 };
/** Sightings past which practice stops sharpening anything. */
export const MAX_TRACKING_FAMILIARITY = 40;
export function spotRange(familiarity) {
    const t = Math.min(1, Math.max(0, familiarity) / MAX_TRACKING_FAMILIARITY);
    return Math.round(SPOT_RANGE.base + (SPOT_RANGE.practised - SPOT_RANGE.base) * t);
}
/**
 * How old a mark reads as, in words.
 *
 * Words rather than a number of hours, and not only because M2-06 banned
 * numbers: a tracker genuinely reads age as a band, and how *narrow* the band
 * is is what practice buys. A novice gets "some time ago" where somebody
 * practised gets "within the hour".
 */
export function ageProse(ageTicks, familiarity) {
    const hours = ageTicks / TICKS_PER_GAME_HOUR;
    const practised = familiarity >= MAX_TRACKING_FAMILIARITY / 2;
    if (!practised) {
        if (hours < 6)
            return 'recent';
        if (hours < 24)
            return 'not fresh';
        return 'old';
    }
    if (hours < 0.5)
        return 'very fresh — minutes, no more';
    if (hours < 2)
        return 'fresh, within the hour or two';
    if (hours < 8)
        return 'some hours old';
    if (hours < 24)
        return 'from earlier in the day';
    return 'a day old at least, or more';
}
/** How weather has spoiled a mark, when it has. */
function conditionProse(sign, weather) {
    if (sign.strength > 0.6)
        return null;
    if (isRaining(weather.kind)) {
        return sign.kind === 'blood' ? 'washing thin' : 'rain-softened';
    }
    return sign.kind === 'blood' ? 'gone dark and tacky' : 'half gone';
}
/** Compass prose, shared with M4-07's gestures — one vocabulary, one file. */
export const BEARINGS = {
    n: 'north',
    ne: 'north-east',
    e: 'east',
    se: 'south-east',
    s: 'south',
    sw: 'south-west',
    w: 'west',
    nw: 'north-west',
};
const KIND_PROSE = {
    print: 'A print',
    trail: 'Bracken bent and broken',
    droppings: 'Droppings',
    blood: 'Blood',
};
/**
 * One mark, as a sentence.
 *
 * Species, direction and age, which is exactly what a tracker gets off the
 * ground and exactly what the ticket asks for. The species is named only to
 * somebody with the practice to tell — a novice reads "something" and has to
 * decide for themselves whether it is worth following, which is a better game
 * than being told.
 */
export function describeSign(sign, tick, weather, familiarity = 0, speciesName) {
    const what = KIND_PROSE[sign.kind];
    const who = familiarity >= MAX_TRACKING_FAMILIARITY / 4 && speciesName ? speciesName : 'something';
    const age = ageProse(Math.max(0, tick - sign.at), familiarity);
    const condition = conditionProse(sign, weather);
    const heading = BEARINGS[sign.direction];
    const tail = condition ? `, ${condition}` : '';
    return sign.kind === 'blood'
        ? `${what} on the ground, ${age}${tail}. Whatever left it was going ${heading}.`
        : `${what} — ${who}, heading ${heading}. ${capitalise(age)}${tail}.`;
}
/** Every mark within `range` of a point, nearest first. */
export function signsNear(signs, x, y, range) {
    const found = [];
    for (const key of Object.keys(signs).sort()) {
        const sign = signs[key];
        if (!sign)
            continue;
        if (Math.max(Math.abs(sign.x - x), Math.abs(sign.y - y)) > range)
            continue;
        found.push(sign);
    }
    return found;
}
/**
 * The most worth reading of the marks on one tile.
 *
 * Blood first, because it is the one that means something is hurt and the one a
 * hunter is most likely to be following on purpose. Otherwise the strongest.
 */
export function bestSignAt(signs, x, y) {
    let best = null;
    for (const kind of SIGN_KINDS) {
        const sign = signs[signKey(x, y, kind)];
        if (!sign)
            continue;
        if (sign.kind === 'blood')
            return sign;
        if (!best || sign.strength > best.strength)
            best = sign;
    }
    return best;
}
/**
 * How long a den's ambient sign takes to build up.
 *
 * Dens accumulate droppings whether or not anything has been seen leaving one,
 * which is the ticket's "find dens by working back along trails": the trail
 * gets you to the area and the ground at the mouth tells you that you have
 * arrived. Without it a den is invisible until something walks out of it.
 */
export const DEN_SIGN_EVERY = TICKS_PER_GAME_DAY / 4;
function clamp01(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.max(0, Math.min(1, value));
}
function capitalise(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
}
/** Whether this actor is running for its life, for the emission rate. */
export function isFleeing(combatant, state) {
    if (state === 'flee')
        return true;
    return combatant?.vitals.staggered === true;
}
/** Total bleed on a body, for the blood-trail rule. `0` for anything unhurt. */
export function bleedOf(combatant) {
    if (!combatant)
        return 0;
    let total = 0;
    for (const wound of combatant.vitals.wounds)
        total += wound.bleedRate;
    return total;
}
/** Sanity: the direction vocabulary this module prints has to cover the sim's. */
export function assertBearingsCover() {
    for (const direction of DIRECTIONS) {
        if (!BEARINGS[direction])
            throw new Error(`no bearing prose for '${direction}'`);
    }
}
/** Sanity: every weather kind decays every sign kind to a positive rate. */
export function assertDecayCovers(kinds) {
    for (const weather of kinds) {
        for (const kind of SIGN_KINDS) {
            const rate = weatherDecay({ kind: weather, since: 0, wind: 'sw', wet: 500 }, kind);
            if (!(rate > 0))
                throw new Error(`'${weather}' gives '${kind}' a non-positive decay rate`);
        }
    }
}
//# sourceMappingURL=sign.js.map